// Plan documents (SBCs, summaries, links) — one component, two modes:
//  - manage: upload files / add external links / delete (plan owners).
//  - list: read-only download/open links (employees, employers, brokers).
// Files download through the authenticated endpoint; links open in a new tab
// and are rendered AS links.

import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Download, ExternalLink, FileText, Link2, Plus, Trash2, Upload } from "lucide-react";

export interface PlanDocumentRow {
  id: number;
  planId: number;
  name: string;
  docType: string;
  fileName: string | null;
  externalUrl: string | null;
  createdAt: string | null;
}

const DOC_TYPES = [
  { value: "sbc", label: "SBC (Summary of Benefits and Coverage)" },
  { value: "summary", label: "Plan Summary" },
  { value: "other", label: "Other" },
];

export function usePlanDocuments(planId: number | null | undefined) {
  return useQuery<PlanDocumentRow[]>({
    queryKey: ["/api/plans", planId, "documents"],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/documents`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: planId != null,
  });
}

export function PlanDocumentsList({
  planId,
  compact = false,
}: {
  planId: number;
  compact?: boolean;
}) {
  const { data: docs = [] } = usePlanDocuments(planId);
  if (docs.length === 0) return null;
  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      {!compact && <Label className="text-xs text-muted-foreground">Documents</Label>}
      {docs.map((d) => (
        <a
          key={d.id}
          href={
            d.externalUrl
              ? d.externalUrl
              : `/api/plans/${planId}/documents/${d.id}/download`
          }
          target={d.externalUrl ? "_blank" : undefined}
          rel="noreferrer"
          className="flex items-center gap-2 text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          {d.externalUrl ? (
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <Download className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate">{d.name}</span>
          {d.docType === "sbc" && (
            <Badge variant="outline" className="text-[10px]">SBC</Badge>
          )}
        </a>
      ))}
    </div>
  );
}

export function PlanDocumentsManager({ planId }: { planId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: docs = [] } = usePlanDocuments(planId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("sbc");
  const [linkUrl, setLinkUrl] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/plans", planId, "documents"] });

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim() && !pendingFile) throw new Error("Give the document a name");
      if (!pendingFile && !linkUrl.trim()) throw new Error("Choose a file or paste a link");
      const form = new FormData();
      form.set("name", name.trim() || pendingFile?.name || "Document");
      form.set("docType", docType);
      if (pendingFile) form.set("file", pendingFile);
      else form.set("externalUrl", linkUrl.trim());
      const res = await fetch(`/api/plans/${planId}/documents`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to add document");
      return body;
    },
    onSuccess: () => {
      invalidate();
      setName("");
      setLinkUrl("");
      setPendingFile(null);
      if (fileInput.current) fileInput.current.value = "";
      toast({ title: "Document added" });
    },
    onError: (e: any) => toast({ title: e?.message || "Failed to add document", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (docId: number) => {
      const res = await fetch(`/api/plans/${planId}/documents/${docId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete document");
    },
    onSuccess: () => invalidate(),
    onError: (e: any) => toast({ title: e?.message || "Failed to delete", variant: "destructive" }),
  });

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Documents (SBC, summaries, links)</Label>
      {docs.length > 0 && (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-md border p-2.5">
              <a
                href={d.externalUrl ? d.externalUrl : `/api/plans/${planId}/documents/${d.id}/download`}
                target={d.externalUrl ? "_blank" : undefined}
                rel="noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline dark:text-blue-400"
              >
                {d.externalUrl ? <Link2 className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                {d.name}
                {d.docType === "sbc" && <Badge variant="outline" className="text-[10px]">SBC</Badge>}
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteMutation.mutate(d.id)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-md border p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input className="mt-1 h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., 2026 SBC" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 items-end">
          <div>
            <Label className="text-xs">Upload file</Label>
            <Input
              ref={fileInput}
              type="file"
              className="mt-1 h-9"
              onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <Label className="text-xs">…or paste a link (shown as a link)</Label>
            <Input
              className="mt-1 h-9"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://carrier.com/sbc.pdf"
              disabled={!!pendingFile}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => addMutation.mutate()}
            disabled={addMutation.isPending}
          >
            {pendingFile ? <Upload className="h-3.5 w-3.5 mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            {addMutation.isPending ? "Adding…" : "Add Document"}
          </Button>
        </div>
      </div>
    </div>
  );
}
